/**
 * WD-4 — Ban user.
 * US-A07 AC5: a duration or permanent, and a recorded reason.
 * Moderator or Super Admin only — the access matrix hides the trigger.
 */
import { Form, Radio } from 'antd'
import { useState } from 'react'
import { useAdminTranslate } from '@/providers/i18n-provider'
import { ConfirmDialog } from './ConfirmDialog'

export type BanDuration = '1d' | '7d' | '30d' | 'permanent'

export interface BanUserDialogProps {
  open: boolean
  userName: string
  onConfirm: (payload: { duration: BanDuration; reason: string }) => Promise<void> | void
  onCancel: () => void
}

const DURATIONS: { value: BanDuration; key: string }[] = [
  { value: '1d', key: 'duration1d' },
  { value: '7d', key: 'duration7d' },
  { value: '30d', key: 'duration30d' },
  { value: 'permanent', key: 'durationPermanent' },
]

export const BanUserDialog = ({ open, userName, onConfirm, onCancel }: BanUserDialogProps) => {
  const translate = useAdminTranslate()
  const [duration, setDuration] = useState<BanDuration>('7d')
  const options = DURATIONS.map(({ value, key }) => ({
    value,
    label: translate(`admin.dialogs.ban.${key}`),
  }))

  return (
    <ConfirmDialog
      open={open}
      danger
      title={translate('admin.dialogs.ban.title', { name: userName })}
      description={translate('admin.dialogs.ban.description')}
      confirmLabel={translate('admin.dialogs.ban.confirm')}
      requireReason
      reasonLabel={translate('admin.dialogs.ban.reasonLabel')}
      onConfirm={reason => onConfirm({ duration, reason: reason ?? '' })}
      onCancel={onCancel}
    >
      <Form layout="vertical">
        <Form.Item label={translate('admin.dialogs.ban.durationLabel')} required>
          <Radio.Group
            options={options}
            value={duration}
            optionType="button"
            onChange={event => setDuration(event.target.value as BanDuration)}
          />
        </Form.Item>
      </Form>
    </ConfirmDialog>
  )
}
